import React from 'react';

interface UserAvatarProps {
    src?: string | null;
    name?: string | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    className?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ src, name, size = 'md', className = '' }) => {
    const getInitials = (userName: string | null) => {
        if (!userName || userName.trim() === '' || userName.toLowerCase() === 'no name') return 'U';
        
        const parts = userName.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    };

    const getGradientColor = (userName: string | null) => {
        const colors = [
            'from-blue-500 to-blue-600',
            'from-purple-500 to-purple-600',
            'from-indigo-500 to-indigo-600',
            'from-rose-500 to-rose-600',
            'from-emerald-500 to-emerald-600',
            'from-amber-500 to-amber-600',
        ];
        
        if (!userName) return colors[0];
        
        let hash = 0;
        for (let i = 0; i < userName.length; i++) {
            hash = userName.charCodeAt(i) + ((hash << 5) - hash);
        }
        
        return colors[Math.abs(hash) % colors.length];
    };

    const sizeClasses = {
        xs: 'w-6 h-6 text-[10px]',
        sm: 'w-8 h-8 text-xs',
        md: 'w-10 h-10 text-sm',
        lg: 'w-12 h-12 text-base',
        xl: 'w-16 h-16 text-lg',
    };

    const isDummy = (url?: string | null) => {
        if (!url) return true;
        const dummyPatterns = [
            'pexels.com',
            'via.placeholder.com',
            'placeholder.com',
            'avatar-placeholder',
            'dummy'
        ];
        return dummyPatterns.some(pattern => url.toLowerCase().includes(pattern));
    };

    if (src && !isDummy(src)) {
        return (
            <img
                src={src}
                alt={name || 'User'}
                className={`${sizeClasses[size]} rounded-full object-cover border border-gray-100 ${className}`}
                onError={(e) => {
                    // Fallback if image fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                    (e.target as HTMLImageElement).parentElement?.classList.add('flex');
                }}
            />
        );
    }

    const initials = getInitials(name);
    const gradient = getGradientColor(name || '');

    return (
        <div 
            className={`${sizeClasses[size]} rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white font-bold tracking-wider border border-white/10 shadow-sm ${className}`}
            title={name || 'User'}
        >
            {initials}
        </div>
    );
};

export default UserAvatar;
